import type { TimelineEntry } from '@compass/client/types/case_record';
import type { User } from '@compass/client/types/user';
import { cn } from '@utils/index';
import { Bot, CirclePlus, Clock, type LucideIcon, Phone, PhoneOff } from 'lucide-react';
import React from 'react';

import { UserAvatar } from '@/components/UserAvatar';
import type { SupportedTimeZone } from '@/constants/date';
import { useStore } from '@/store';
import { formatDurationSeconds, TIMEZONE_MAP } from '@/utils/date';
import { formatFieldName } from '@/utils/string';

/** Returns "11th May 2026 4:29pm" in the selected timezone. */
function formatActivityTimestamp(iso: string | undefined, timeZone: SupportedTimeZone): string {
  if (!iso) return '';
  const dateObj = new Date(iso);
  if (isNaN(dateObj.getTime())) return '';

  const ianaTimeZone = TIMEZONE_MAP[timeZone];

  const parts = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: ianaTimeZone,
  }).formatToParts(dateObj);

  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  const day = Number(get('day'));
  const month = get('month');
  const year = get('year');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod').toLowerCase();

  const ordinal = (n: number): string => {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    switch (n % 10) {
      case 1:
        return `${n}st`;
      case 2:
        return `${n}nd`;
      case 3:
        return `${n}rd`;
      default:
        return `${n}th`;
    }
  };

  return `${ordinal(day)} ${month} ${year} ${hour}:${minute}${dayPeriod}`;
}

interface CaseTimelineEntryProps {
  entry: TimelineEntry;
  user?: User;
  usersMap?: Map<string, User>;
  className?: string;
}

interface EventMeta {
  label: string;
}

const EVENT_META: Record<string, EventMeta> = {
  'case.created': { label: 'Created' },
  'case.state.changed': { label: 'State Changed' },
  'case.human_audit.in_progress': { label: 'Human Audit In Progress' },
  'case.assigned': { label: 'Assigned' },
  'case.workflow.started': { label: 'Workflow Started' },
  'case.workflow.done': { label: 'Workflow Done' },
  'case.workflow.paused': { label: 'Workflow Paused' },
  'case.note.added': { label: 'Note Added' },
  'case.task.created': { label: 'Task Created' },
  'case.task.completed': { label: 'Task Completed' },
  'case.text.sent': { label: 'Text Sent' },
  'case.email.created': { label: 'Email Created' },
  'case.email.sent': { label: 'Email Sent' },
  'case.call.started': { label: 'Call Started' },
  'case.call.completed': { label: 'Call Completed' },
  'case.call.cancelled': { label: 'Call Cancelled' },
  'case.workflow.cancelled': { label: 'Case Cancelled' },
  'case.deleted': { label: 'Deleted' },
  'case.attachment.added': { label: 'Attachment Added' },
};

const DEFAULT_META: EventMeta = { label: 'Event' };

function metaFor(type: string | undefined): EventMeta {
  if (!type) return DEFAULT_META;
  if (EVENT_META[type]) return EVENT_META[type];
  // Fall back to deriving a label from the dot-separated suffix.
  const last = type.split('.').pop() || type;
  return { label: formatFieldName(last) };
}

// Timeline phrasing for who ended a call (BV-2130). Deliberately a closed map
// rather than the popover's title-casing fallback: this reads mid-sentence, and
// an unrecognised value from another producer would land as a garbled clause.
// An unmapped value simply drops the clause.
const TERMINATED_BY_PHRASES: Record<string, string> = {
  rep: 'rep hung up',
  payer: 'payer hung up',
  unknown: 'who hung up is unknown',
};

/**
 * Best-effort description from the event detail payload. Each event type stores
 * its data under a different key in detail (matching the protobuf oneof arms).
 */
function describeDetail(
  type: string | undefined,
  detail: Record<string, any> | undefined,
  usersMap?: Map<string, User>,
): string | null {
  if (!detail || !type) return null;

  const nameFor = (id: string | undefined): string | null => {
    if (!id) return null;
    const u = usersMap?.get(id);
    return u?.name || u?.email || null;
  };

  switch (type) {
    case 'case.created':
      // Title only ("Case created manually"); the creator/assignee are shown as
      // two subtext lines by the component (see buildCreatedLines).
      return caseCreatedTitle(detail.created?.creation_method);
    case 'case.state.changed': {
      const from = detail.state_changed?.from_state;
      const to = detail.state_changed?.to_state;
      // BI-1246: entering human audit is the "Needed" phase (the "In Progress"
      // phase is a separate backend event, case.human_audit.in_progress). Keep the
      // from-state so the transition still reads e.g. "from Human Call".
      if (to === 'human_audit') {
        return from
          ? `State changed from ${formatFieldName(from)} to Human Audit Needed`
          : 'State changed to Human Audit Needed';
      }
      if (from && to)
        return `State changed from ${formatFieldName(from)} to ${formatFieldName(to)}`;
      if (to) return `State changed to ${formatFieldName(to)}`;
      return null;
    }
    case 'case.human_audit.in_progress':
      // Backend event emitted when the auditor starts the session; render it as
      // the needed → in-progress phase transition (auditor via Event.actor_id).
      return 'State changed from Human Audit Needed to Human Audit In Progress';
    case 'case.assigned': {
      const toName = nameFor(detail.assigned?.to_user_id);
      const fromName = nameFor(detail.assigned?.from_user_id);
      if (toName && fromName) return `Assigned to ${toName} (from ${fromName})`;
      if (toName) return `Assigned to ${toName}`;
      return null;
    }
    case 'case.workflow.done':
      return formatEnumLike(detail.workflow_done?.status);
    case 'case.email.sent': {
      const provider = detail.email_sent?.provider;
      return provider ?? null;
    }
    case 'case.call.started': {
      const callType = formatEnumLike(detail.call_started?.call_type);
      return callType ? `${callType} started` : null;
    }
    case 'case.call.completed': {
      const callType = formatEnumLike(detail.call_completed?.call_type);
      const duration = detail.call_completed?.duration;
      // BV-2130. Absent on entries written before the field existed, and on any
      // call whose ending nothing could attribute — the clause just drops.
      const endedBy = TERMINATED_BY_PHRASES[detail.call_completed?.terminated_by ?? ''];
      const suffix = endedBy ? ` — ${endedBy}` : '';
      if (callType && duration) {
        return `${callType} completed within ${formatDurationSeconds(duration)}${suffix}`;
      }
      if (callType) {
        return `${callType} completed${suffix}`;
      }
      return null;
    }
    case 'case.call.cancelled': {
      // BV-1994: the operator who hung up the in-flight AI call. The actor is
      // also on entry.actor_id, so the component's "by {actor}" line covers
      // attribution — this title just names the action.
      const callType = formatEnumLike(detail.call_cancelled?.call_type);
      return callType ? `${callType} cancelled` : 'Call cancelled';
    }
    case 'case.workflow.cancelled':
      // BV-1994: user-initiated Cancel Case. Worded as the request rather than
      // the outcome — the workflow actually closing is a separate, later
      // case.workflow.completed event with status "cancelled".
      return 'Case cancellation requested';
    case 'case.deleted':
      return formatEnumLike(detail.deleted?.reason);
    case 'case.attachment.added':
      return formatEnumLike(detail.attachment_add?.asset_type);
    default:
      // For events whose detail payload only carries IDs (note_id, run_id, task_id,
      // text_id, email_id, etc.) we fall back to the static event label rather
      // than showing a raw UUID-looking string in the timeline.
      return null;
  }
}

/**
 * Format snake_case / lowercase enum-like values (e.g. "ai_call" → "Ai Call").
 * Leaves already-formatted values (containing uppercase or space) alone so we
 * don't mangle things like "Eligibility Check" that come pre-formatted.
 */
function formatEnumLike(value: string | undefined | null): string | null {
  if (!value) return null;
  if (/[A-Z\s]/.test(value)) return value;
  return formatFieldName(value);
}

/** Human-readable title for the case-creation origin node, driven by creation_method. */
function caseCreatedTitle(method: string | undefined | null): string {
  switch (method) {
    case 'manual':
      return 'Case created manually';
    case 'ingestion':
      return 'Case created via ingestion';
    case 'active_calls_ingestion':
      return 'Case created via active call ingestion';
    case 'api':
      return 'Case created via API';
    default: {
      const formatted = formatEnumLike(method);
      return formatted ? `Case created via ${formatted}` : 'Case created';
    }
  }
}

/** Resolve a user id to a display name (name → email), or null when unknown. */
function resolveUserName(
  usersMap: Map<string, User> | undefined,
  id: string | undefined,
): string | null {
  if (!id) return null;
  const u = usersMap?.get(id);
  return u?.name || u?.email || null;
}

/**
 * Subtext lines for the case-creation node: the actor who created the case and
 * the user it was assigned to on creation.
 */
function buildCreatedLines(
  entry: TimelineEntry,
  actorName: string,
  usersMap?: Map<string, User>,
): string[] {
  const assignedName = resolveUserName(usersMap, entry.detail?.created?.assigned_user_id);
  return [`created by: ${actorName}`, `assigned to: ${assignedName ?? 'Unassigned'}`];
}

function getActorName(entry: TimelineEntry, user?: User): { name: string; isSystem: boolean } {
  if (!entry.actor_id) return { name: 'System', isSystem: true };
  if (user) return { name: user.name || user.email || 'Unknown', isSystem: false };
  return { name: 'Unknown', isSystem: false };
}

/** Icon shown inside the non-avatar marker circle for a given event type. */
function markerIconFor(entry: TimelineEntry): LucideIcon {
  if (entry.type === 'case.created') return CirclePlus;
  if (entry.type === 'case.call.cancelled') return PhoneOff;
  if (entry.type === 'case.call.started' || entry.type === 'case.call.completed') {
    const callDetail = entry.detail?.call_started ?? entry.detail?.call_completed;
    return callDetail?.call_type === 'ai_call' ? Bot : Phone;
  }
  return Clock;
}

interface TimelineMarkerProps {
  entry: TimelineEntry;
  isSystem: boolean;
  user?: User;
  actorName: string;
}

/**
 * Leading dot of a timeline row. Actor-driven events show the actor's avatar;
 * system events and the case-creation origin show an icon circle. The created
 * event always gets a distinct primary-tinted CirclePlus (even when a user
 * created it) so the timeline's start stands out — attribution still shows in
 * the "by {actor}" body line.
 */
const TimelineMarker: React.FC<TimelineMarkerProps> = ({ entry, isSystem, user, actorName }) => {
  const isCreated = entry.type === 'case.created';
  if (!isSystem && !isCreated) {
    return (
      <UserAvatar name={user?.name || actorName} email={user?.email} userId={user?.id} size="sm" />
    );
  }
  const Icon = markerIconFor(entry);
  return (
    <div
      className={cn(
        'h-6 w-6 rounded-full flex items-center justify-center border',
        isCreated ? 'bg-primary/10 border-primary/30' : 'bg-muted border-border',
      )}
    >
      <Icon className={cn('h-3 w-3', isCreated ? 'text-primary' : 'text-muted-foreground')} />
    </div>
  );
};

export const CaseTimelineEntry: React.FC<CaseTimelineEntryProps> = React.memo(
  ({ entry, user, usersMap, className }) => {
    const { getTimeZone } = useStore();
    const timeZone = getTimeZone();
    const meta = metaFor(entry.type);
    const detail = describeDetail(entry.type, entry.detail, usersMap);
    const { name: actorName, isSystem } = getActorName(entry, user);

    // Show the action that took place as the title (state transition / event
    // description) and attribute it to the actor in the body. Falls back to the
    // event label when no specific detail is available. Hide the actor line
    // entirely when there is no actor_id (don't show "by System").
    const title = detail ?? meta.label;
    // The case-creation node shows creator + assignee on their own lines; every
    // other event attributes the action to its actor with a single "by" line.
    const isCreated = entry.type === 'case.created';
    const createdLines = isCreated ? buildCreatedLines(entry, actorName, usersMap) : null;
    const body = isSystem ? null : `by ${actorName}`;
    // Only a Move to Human Audit note carries its text (BV-2657): the reason the
    // Program Admin gave. Every other note event is id-only.
    const noteContent =
      entry.type === 'case.note.added'
        ? (entry.detail?.note_added?.content as string | undefined)
        : undefined;

    return (
      <div className={cn('relative flex items-start gap-3', className)}>
        <div className="relative z-10 shrink-0">
          <TimelineMarker entry={entry} isSystem={isSystem} user={user} actorName={actorName} />
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-xs font-medium text-foreground break-words" title={title}>
            {title}
          </p>
          {noteContent && (
            <p className="text-xs text-foreground/80 mt-1 whitespace-pre-wrap break-words">
              {noteContent}
            </p>
          )}
          {createdLines
            ? createdLines.map(line => (
                <p key={line} className="text-xs text-muted-foreground mt-1 break-words">
                  {line}
                </p>
              ))
            : body && <p className="text-xs text-muted-foreground mt-1 break-words">{body}</p>}
          <p className="text-[10px] text-muted-foreground mt-1 shrink-0">
            {formatActivityTimestamp(entry.created_at, timeZone)}
          </p>
        </div>
      </div>
    );
  },
);

CaseTimelineEntry.displayName = 'CaseTimelineEntry';
